import type { AnalystChartSpec, AnalystTableSpec } from "./report-analyst";
import type { StudyFigureQuestion } from "./study-figures";

/** Exact persisted artifact revision a readiness notification opens. */
export interface StudyArtifactAttentionTarget {
  threadToken: string;
  originMessageToken: string;
  studyToken: string;
  artifactToken: string;
  artifactRevision: number;
}

export interface StudyQuestionnaireAttentionTarget {
  stage: "questions";
  threadToken: string;
  originMessageToken: string;
  draftToken: string;
  draftRevision: number;
}

export interface QuickStudyQuestionAttentionTarget {
  kind: "quick_study";
  stage: "questions";
  threadToken: string;
  originMessageToken: string;
  draftToken: string;
  draftRevision: number;
}
export interface QuickStudyResultsAttentionTarget {
  kind: "quick_study";
  stage: "results";
  threadToken: string;
  originMessageToken: string;
  draftToken: string;
  draftRevision: number;
  runToken: string;
}
export type QuickStudyAttentionTarget = QuickStudyQuestionAttentionTarget | QuickStudyResultsAttentionTarget;
export type StudyAttentionTarget = StudyArtifactAttentionTarget | StudyQuestionnaireAttentionTarget | QuickStudyAttentionTarget;

export function isQuickStudyAttentionTarget(target: StudyAttentionTarget): target is QuickStudyAttentionTarget {
  return 'kind' in target && target.kind === 'quick_study';
}
export function isStudyQuestionnaireAttentionTarget(target: StudyAttentionTarget): target is StudyQuestionnaireAttentionTarget {
  return !isQuickStudyAttentionTarget(target) && 'stage' in target && target.stage === 'questions';
}
export function isStudyArtifactAttentionTarget(target: StudyAttentionTarget): target is StudyArtifactAttentionTarget {
  return 'studyToken' in target;
}

export const STUDY_ARTIFACT_ACKNOWLEDGMENT_REASONS = ["viewed", "dismissed", "posted"] as const;
export type StudyArtifactAttentionAcknowledgmentReason = (typeof STUDY_ARTIFACT_ACKNOWLEDGMENT_REASONS)[number];

export interface StudyArtifactAttentionReceipt {
  attentionToken: string;
  target: StudyAttentionTarget;
  createdAt: string;
  acknowledgedAt: string | null;
  acknowledgmentReason: StudyArtifactAttentionAcknowledgmentReason | null;
}

export interface StudyArtifactAttentionListResponse {
  items: Array<StudyArtifactAttentionReceipt>;
}

/** One saved question, using the regular study figure or its existing analyst exhibits. */
export interface StudyFindingsQuestion {
  questionId: string;
  title: string;
  body: string;
  baseN: number;
  lowBase: boolean;
  nativePlot?: StudyFigureQuestion["plots"][string];
  charts: AnalystChartSpec[];
  tables: AnalystTableSpec[];
}

/** Persisted canonical facts and existing analyst exhibits; no new writer pass. */
export interface StudyFindingsView {
  target: StudyArtifactAttentionTarget;
  title: string;
  body: string;
  charts: AnalystChartSpec[];
  tables: AnalystTableSpec[];
  questions?: StudyFindingsQuestion[];
  canPost: boolean;
}

export interface StudyFindingsPostResult {
  target: StudyArtifactAttentionTarget;
  messageToken: string;
  posted: boolean;
}
